import CONFIG from 'Config/settings';
import { ReduxStore } from 'Redux';
import { setUserLoginAction } from 'Redux/auth/authActions';
import { getSession, login } from 'Redux/auth/authThunks';
import { clearFormAction, setFormErrorsAction } from 'Redux/forms/formsActions';
import { setProfileAction } from 'Redux/profile/profileActions';
import { PROFILE_TYPES } from 'Redux/profile/profileConstants';
import { loadingAction } from 'Redux/userInterface/userInterfaceActions';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import {
  setEmailVerifiedAction,
  setLoginIdentifierAction,
  setOTPSentErrorAction,
  setOTPStatusAction
} from 'Redux/verification/verificationActions';
import Routes from 'Routes/profileRoutes';
import {
  Auth0Service,
  CIAMService,
  ProfileService,
  SalutationService
} from 'Services/ServiceFactory';
import analytics from 'Utils/analytics/analyticsWrapper';
import {
  appendAuthTokenToUrl,
  readAuthTokenCookie,
  writeSessionCookie
} from 'Utils/auth/authUtils';
import {
  mapBackendException,
  sendAnalyticsErrors
} from 'Utils/errors/errorUtils';
import { checkForProfanityWords } from 'Utils/profanityFilter/profanityFilterUtils';
import { events, publishEvent } from 'Utils/pubsub';
import { captureSentry } from 'Utils/sentry';
import { isValidIKEAUrl } from 'Utils/url/urlUtils';
import { setDeliveryAddressesAction } from '../addresses/addressesActions';

const updateIkeaSessionCookie = async updatedProfile => {
  if (window.ikea?.cookieConsent?.hasConsent(3)) {
    const firstNameInitial = updatedProfile.firstName
      ? updatedProfile.firstName.charAt(0).toUpperCase()
      : undefined;
    const lastNameInitial = updatedProfile.lastName
      ? updatedProfile.lastName.charAt(0).toUpperCase()
      : undefined;
    const nameInitials =
      firstNameInitial && lastNameInitial
        ? `${firstNameInitial}${lastNameInitial}`
        : '';
    await writeSessionCookie(
      JSON.stringify({
        firstName: updatedProfile.firstName,
        nameInitials,
        preferredStore: updatedProfile.preferredStore
      })
    );
    await publishEvent(events.USER_INFO_AVAILABLE);
  }
};

export const getProfile = () => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_GET, true));

      const { loggedIn } = ReduxStore.getState().auth;

      if (!loggedIn) {
        await dispatch(login());
      }

      const { userId } = ReduxStore.getState().profile;
      const { partyUid } = ReduxStore.getState().auth;

      if (!userId && !partyUid) {
        // User did not have a valid session and
        // is redirected to CIAM hosted login page.
        return;
      }
      // undefined for now, but when setting up China, this value has to be sent
      const profileResp = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      if (!profileResp) {
        throw Error('No profile response');
      }

      if (CONFIG.FEATURE.ENABLE_SALUTATION_SERVICE) {
        const { firstNamePhonetic } = profileResp;
        if (!firstNamePhonetic) {
          const phoneticName = await SalutationService.getSalutationForFirstName(
            profileResp.firstName,
            profileResp.lastName,
            profileResp.genderCode
          );
          profileResp.firstNamePhonetic = phoneticName;
        }
      }

      await dispatch(setProfileAction(profileResp));
      await dispatch(setDeliveryAddressesAction(profileResp.deliveryAddresses));
    } catch (e) {
      captureSentry(e, {
        extra: { profileType: ReduxStore.getState().profile.profileType }
      });
      window.location = Routes.ERROR;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_GET, false));
    }
  };
};

export const updateProfile = (profile, formId, persistFormState, mode) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      analytics.userUpdatedProfile(profile.profileType);
      const currentProfile = ReduxStore.getState().profile;
      if (CONFIG.FEATURE.ENABLE_SALUTATION_SERVICE) {
        const firstNamePhonetic = await SalutationService.getSalutationForFirstName(
          profile.firstName,
          profile.lastName,
          profile.genderCode
        );
        // eslint-disable-next-line no-param-reassign
        profile.firstNamePhonetic = firstNamePhonetic;
      }
      if (CONFIG.FEATURE.ENABLE_PROFANITY_FILTER_SERVICE) {
        // Check if the data input by user contains profanity words
        await checkForProfanityWords(
          ReduxStore.getState().forms[`${formId}-values`]
        );
      }
      const { partyUid } = ReduxStore.getState().auth;
      const { userId } = ReduxStore.getState().profile;
      await ProfileService.update(currentProfile, profile, partyUid, mode);
      const updatedProfile = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      await updateIkeaSessionCookie(updatedProfile);
      dispatch(setProfileAction(updatedProfile));
      if (persistFormState) {
        dispatch(setFormErrorsAction(formId, []));
      } else {
        dispatch(clearFormAction(formId));
      }
      await analytics.userProfileFormUpdate({
        status: 'completed',
        form: formId
      });
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};

export const upgradeProfile = (profile, formId, from = '') => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPGRADE, true));
      analytics.userUpgradedProfile();
      const { partyUid } = ReduxStore.getState().auth;
      const currentProfile = ReduxStore.getState().profile;
      if (CONFIG.FEATURE.ENABLE_SALUTATION_SERVICE) {
        const firstNamePhonetic = await SalutationService.getSalutationForFirstName(
          profile.firstName,
          profile.lastName,
          profile.genderCode
        );
        // eslint-disable-next-line no-param-reassign
        profile.firstNamePhonetic = firstNamePhonetic;
      }
      if (CONFIG.FEATURE.ENABLE_PROFANITY_FILTER_SERVICE) {
        // Check if the data input by user contains profanity words
        await checkForProfanityWords(
          ReduxStore.getState().forms[`${formId}-values`]
        );
      }
      await ProfileService.upgrade(
        currentProfile,
        { ...profile, profileType: PROFILE_TYPES.FAMILY },
        partyUid
      );
      await Auth0Service.refreshToken();
      if (isValidIKEAUrl(from)) {
        window.location = appendAuthTokenToUrl(from);
        return true;
      }
      dispatch(
        setProfileAction({ ...profile, profileType: PROFILE_TYPES.FAMILY })
      );
      dispatch(loadingAction(LOADING.PROFILE_UPGRADE, false));
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      dispatch(loadingAction(LOADING.PROFILE_UPGRADE, false));
      return false;
    }
  };
};

export const updateUserLogin = (data, formId) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));

      const { profile } = ReduxStore.getState();
      const oldUserLogin = ReduxStore.getState().auth.userLogin;
      const reqData = {
        userId: profile.userId,
        userLogin: oldUserLogin,
        password: data.password,
        newUserLogin: data.email.toLowerCase(),
        loginType: 'EMAIL'
      };
      // User updated userLogin. This will also update email contact method.
      if (
        !profile.email ||
        data.email.toLowerCase() !== profile.email.toLowerCase()
      ) {
        const updateUserLoginResp = await ProfileService.updateLogonId(reqData);

        if (updateUserLoginResp.error) {
          throw Error();
        }
        analytics.userUpdatedUserLogin(profile.profileType);
        await dispatch(setUserLoginAction(reqData.newUserLogin));
        const updatedProfile = {
          ...profile,
          email: reqData.newUserLogin
        };
        await dispatch(setProfileAction(updatedProfile));

        const authResp = await getSession();
        await dispatch(setEmailVerifiedAction(authResp.user.emailVerified));
      }

      const mobileOrPhoneChanged =
        data.mobile !== profile.mobile || data.landline !== profile.landline;
      if (mobileOrPhoneChanged) {
        const authResp = await getSession();
        let { newUserLogin } = reqData;
        if (authResp.user.identifier === 'mobile') {
          newUserLogin = ReduxStore.getState().profile.email;
          const updateUserLoginResp = await ProfileService.updateLogonId({
            ...reqData,
            password: undefined,
            newUserLogin
          });

          if (updateUserLoginResp.error) {
            throw Error();
          }
          await dispatch(setLoginIdentifierAction('email'));
        }
        await dispatch(updateProfile(data, newUserLogin, true, 'SYNC'));
        if (mobileOrPhoneChanged) {
          await dispatch(setOTPSentErrorAction(undefined));
          await dispatch(setOTPStatusAction(false));
        }
      }
      if (formId) {
        dispatch(clearFormAction(formId));
      }
      return true;
    } catch (e) {
      if (formId) {
        dispatch(setFormErrorsAction(formId, mapBackendException(e)));
        return false;
      }
      return { errors: mapBackendException(e) };
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};

export const updateHouseHoldInfo = (
  homeAndInterest,
  formId,
  persistFormState
) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      // analytics.userUpdatedProfile(profile.profileType);
      const { partyUid } = ReduxStore.getState().auth;
      const { userId } = ReduxStore.getState().profile;
      await ProfileService.updateHomeAndInterests(homeAndInterest, partyUid);
      const updatedProfile = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      dispatch(setProfileAction(updatedProfile));
      if (persistFormState) {
        dispatch(setFormErrorsAction(formId, []));
      } else {
        dispatch(clearFormAction(formId));
      }
      await analytics.userProfileFormUpdate({
        status: 'completed',
        form: formId
      });
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};
export const upsertContactMethod = (
  contactMethod,
  formId,
  persistFormState
) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      // analytics.userUpdatedProfile(profile.profileType);
      const { partyUid } = ReduxStore.getState().auth;
      const { userId } = ReduxStore.getState().profile;
      await ProfileService.upsertContactMethod(contactMethod, partyUid);
      const updatedProfile = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      dispatch(setProfileAction(updatedProfile));
      if (persistFormState) {
        dispatch(setFormErrorsAction(formId, []));
      } else {
        dispatch(clearFormAction(formId));
      }
      await analytics.userProfileFormUpdate({
        status: 'completed',
        form: formId
      });
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};
export const updateCustomerPersonalInfo = (
  profile,
  formId,
  persistFormState
) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      // analytics.userUpdatedProfile(profile.profileType);
      const { partyUid } = ReduxStore.getState().auth;
      const { userId } = ReduxStore.getState().profile;
      const currentProfile = ReduxStore.getState().profile;
      if (CONFIG.FEATURE.ENABLE_PROFANITY_FILTER_SERVICE) {
        // Check if the data input by user contains profanity words
        await checkForProfanityWords(
          ReduxStore.getState().forms[`${formId}-values`]
        );
      }
      await ProfileService.updateCustomerPersonalInfo(profile, currentProfile);
      const updatedProfile = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      await updateIkeaSessionCookie(updatedProfile);
      dispatch(setProfileAction(updatedProfile));
      if (persistFormState) {
        dispatch(setFormErrorsAction(formId, []));
      } else {
        dispatch(clearFormAction(formId));
      }
      await analytics.userProfileFormUpdate({
        status: 'completed',
        form: formId
      });
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};
export const updateProfileSettings = (profile, formId, persistFormState) => {
  return async dispatch => {
    try {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, true));
      analytics.userUpdatedProfile(profile.profileType);
      const currentProfile = ReduxStore.getState().profile;

      const { partyUid } = ReduxStore.getState().auth;
      const { userId } = ReduxStore.getState().profile;
      await ProfileService.updateProfileSettings(
        currentProfile,
        profile,
        partyUid
      );
      const updatedProfile = await ProfileService.getProfile(
        userId,
        undefined,
        partyUid
      );
      await updateIkeaSessionCookie(updatedProfile);
      dispatch(setProfileAction(updatedProfile));
      if (persistFormState) {
        dispatch(setFormErrorsAction(formId, []));
      } else {
        dispatch(clearFormAction(formId));
      }
      await analytics.userProfileFormUpdate({
        status: 'completed',
        form: formId
      });
      return true;
    } catch (e) {
      await sendAnalyticsErrors(e, formId);
      dispatch(setFormErrorsAction(formId, mapBackendException(e)));
      return false;
    } finally {
      dispatch(loadingAction(LOADING.PROFILE_UPDATE, false));
    }
  };
};

// Factory for CIAM redirect thunks — each follows the same shape:
// read partyUid + token, call the CIAM service method, validate the URL.
const createCiamRedirectThunk = serviceCall => () => async () => {
  try {
    const { partyUid } = ReduxStore.getState().auth;
    const authTokenCookie = readAuthTokenCookie();
    const redirectURL = await serviceCall(partyUid, authTokenCookie);
    return isValidIKEAUrl(redirectURL) ? redirectURL : false;
  } catch (e) {
    return false;
  }
};

export const getRedirectURLForUpdateMobile = createCiamRedirectThunk(
  (partyUid, token) =>
    CIAMService.getRedirectURLForUpdateMobile(partyUid, token)
);

export const getRedirectURLForUpdateEmail = createCiamRedirectThunk(
  (partyUid, token) => CIAMService.getRedirectURLForUpdateEmail(partyUid, token)
);

export const getRedirectURLForDeleteProfile = createCiamRedirectThunk(
  (partyUid, token) =>
    CIAMService.getRedirectURLForDeleteProfile(partyUid, token)
);

export const getRedirectURLForUpdatePassword = createCiamRedirectThunk(
  (partyUid, token) =>
    CIAMService.getRedirectURLForUpdatePassword(partyUid, token)
);

export const getRedirectURLForEmailVerification = createCiamRedirectThunk(
  (partyUid, token) =>
    CIAMService.getRedirectURLForEmailVerification(partyUid, token)
);
